import { DAY_KEYS, type WeeklyHours, type DaySchedule } from './fields';

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function emptyWeeklyHours(): WeeklyHours {
  return {
    monday: { open: false, intervals: [] },
    tuesday: { open: false, intervals: [] },
    wednesday: { open: false, intervals: [] },
    thursday: { open: false, intervals: [] },
    friday: { open: false, intervals: [] },
    saturday: { open: false, intervals: [] },
    sunday: { open: false, intervals: [] },
  };
}

function defaultOpenSchedule(): DaySchedule {
  return { open: true, intervals: [{ from: '09:00', to: '18:00' }] };
}

// Opening-hours editor: one aligned row per day — day name (left, never
// truncated), an open/closed switch, then the time intervals inline with
// add/remove. Quick-fill buttons copy a day across the week. The picker works in
// the structured WeeklyHours shape; edit.ts serializes it to the flat display
// map the apps read (fields.ts:weeklyHoursToDisplayMap) on save.
export function mountHoursPicker(
  container: HTMLElement,
  initial: WeeklyHours | null,
  onChange: (next: WeeklyHours) => void,
): void {
  const state: WeeklyHours = initial ? structuredClone(initial) : emptyWeeklyHours();

  const emit = (): void => onChange(structuredClone(state));

  const renderInterval = (
    day: keyof WeeklyHours,
    iv: { from: string; to: string },
    idx: number,
  ): HTMLElement => {
    const sched = state[day];
    const cell = document.createElement('div');
    cell.className = 'hours-interval';

    const from = document.createElement('input');
    from.type = 'time';
    from.value = iv.from;
    from.setAttribute('aria-label', `${capitalize(day)} opening time ${idx + 1}`);
    from.addEventListener('change', () => {
      iv.from = from.value;
      emit();
    });

    const sep = document.createElement('span');
    sep.className = 'hours-sep';
    sep.textContent = '–';
    sep.setAttribute('aria-hidden', 'true');

    const to = document.createElement('input');
    to.type = 'time';
    to.value = iv.to;
    to.setAttribute('aria-label', `${capitalize(day)} closing time ${idx + 1}`);
    to.addEventListener('change', () => {
      iv.to = to.value;
      emit();
    });

    const remove = document.createElement('button');
    remove.type = 'button';
    remove.className = 'hours-remove';
    remove.textContent = '×';
    remove.title = 'Remove these hours';
    remove.setAttribute('aria-label', `Remove ${capitalize(day)} hours ${idx + 1}`);
    remove.addEventListener('click', () => {
      sched.intervals.splice(idx, 1);
      if (sched.intervals.length === 0) sched.open = false;
      render();
      emit();
    });

    cell.append(from, sep, to, remove);
    return cell;
  };

  const renderDayRow = (day: keyof WeeklyHours): HTMLElement => {
    const sched = state[day];
    const row = document.createElement('div');
    row.className = 'hours-row';
    if (!sched.open) row.classList.add('is-closed');

    const label = document.createElement('span');
    label.className = 'hours-day';
    label.id = `hours-${day}-label`;
    label.textContent = capitalize(day);

    // Open/closed switch (checkbox styled as a sliding toggle).
    const sw = document.createElement('label');
    sw.className = 'switch';
    const checkbox = document.createElement('input');
    checkbox.type = 'checkbox';
    checkbox.checked = sched.open;
    checkbox.setAttribute('aria-labelledby', label.id);
    const track = document.createElement('span');
    track.className = 'switch-track';
    track.setAttribute('aria-hidden', 'true');
    checkbox.addEventListener('change', () => {
      sched.open = checkbox.checked;
      if (checkbox.checked && sched.intervals.length === 0) {
        sched.intervals = [{ from: '09:00', to: '18:00' }];
      }
      render();
      emit();
    });
    sw.append(checkbox, track);

    const times = document.createElement('div');
    times.className = 'hours-row-times';
    if (sched.open) {
      sched.intervals.forEach((iv, idx) => times.appendChild(renderInterval(day, iv, idx)));
      const add = document.createElement('button');
      add.type = 'button';
      add.className = 'hours-add';
      add.textContent = '+ Add hours';
      add.addEventListener('click', () => {
        sched.intervals.push({ from: '14:00', to: '18:00' });
        render();
        emit();
      });
      times.appendChild(add);
    } else {
      const closed = document.createElement('span');
      closed.className = 'hours-closed';
      closed.textContent = 'Closed';
      times.appendChild(closed);
    }

    row.append(label, sw, times);
    return row;
  };

  const copyTo = (src: keyof WeeklyHours, targets: Array<keyof WeeklyHours>): void => {
    for (const t of targets) state[t] = structuredClone(state[src]);
    render();
    emit();
  };

  const renderQuickFill = (): HTMLElement => {
    const bar = document.createElement('div');
    bar.className = 'hours-quickfill';

    const lbl = document.createElement('span');
    lbl.className = 'hours-quickfill-label';
    lbl.textContent = 'Quick-fill:';
    bar.appendChild(lbl);

    const weekday = document.createElement('button');
    weekday.type = 'button';
    weekday.className = 'hours-copy-btn';
    weekday.textContent = 'Copy Monday to Tue–Fri';
    weekday.addEventListener('click', () =>
      copyTo('monday', ['tuesday', 'wednesday', 'thursday', 'friday']),
    );

    const weekend = document.createElement('button');
    weekend.type = 'button';
    weekend.className = 'hours-copy-btn';
    weekend.textContent = 'Copy Saturday to Sunday';
    weekend.addEventListener('click', () => copyTo('saturday', ['sunday']));

    bar.append(weekday, weekend);
    return bar;
  };

  function render(): void {
    container.innerHTML = '';
    const wrap = document.createElement('div');
    wrap.className = 'hours-editor';
    for (const day of DAY_KEYS) wrap.appendChild(renderDayRow(day));
    wrap.appendChild(renderQuickFill());
    container.appendChild(wrap);
  }

  render();
}

export function validateWeeklyHours(hours: WeeklyHours): string | null {
  for (const day of DAY_KEYS) {
    const sched = hours[day];
    if (!sched.open) continue;
    const ivs = sched.intervals;
    for (let i = 0; i < ivs.length; i++) {
      const iv = ivs[i];
      if (!iv) continue;
      if (iv.from >= iv.to) {
        return `${capitalize(day)}: ${iv.from}–${iv.to} (from must be before to)`;
      }
      for (let j = i + 1; j < ivs.length; j++) {
        const other = ivs[j];
        if (!other) continue;
        if (iv.from < other.to && other.from < iv.to) {
          return `${capitalize(day)}: overlapping ${iv.from}–${iv.to} and ${other.from}–${other.to}`;
        }
      }
    }
  }
  return null;
}

export { emptyWeeklyHours, defaultOpenSchedule };
